import templateUrl from './form-choice-modal.component.html';
import { InfoModal } from '../info-modal/info-modal.component';

// Generic single-choice confirmation modal for onboarding forms. Reusable across states:
// pass a title/prompt + option list; optional red `warning` (with a required asterisk),
// `submitLabel`, `cancelLabel`, `showCancel`, and `requireValue` (submit enabled only when
// that value is chosen). Unselected required options get the shared orange :invalid border from
// the global .control styling - no per-modal outline (that stacked into a double ring). Resolves to the chosen
// value; dismisses with 'cancel' on the Cancel button.
export const FormChoiceModalComponent = {
    templateUrl,
    controller: FormChoiceModalController,
    controllerAs: 'vm',
    bindings: {
        resolve: '<',
        close: '&',
        dismiss: '&'
    }
};

function FormChoiceModalController() {
    'ngInject';
    const vm = this;
    vm.ok = ok;
    vm.cancel = cancel;
    vm.canSubmit = canSubmit;
    vm.onCheck = onCheck;
    vm.checked = false;

    vm.$onInit = function () {
        vm.title = vm.resolve?.title ?? '';
        vm.prompt = vm.resolve?.prompt ?? '';
        vm.warning = vm.resolve?.warning ?? '';                 // red warning text + required asterisk
        vm.options = vm.resolve?.options ?? [];                 // [{ value, label }]
        vm.submitLabel = vm.resolve?.submitLabel ?? 'Continue';
        vm.cancelLabel = vm.resolve?.cancelLabel ?? 'Cancel';
        vm.showCancel = vm.resolve?.showCancel ?? false;
        vm.requireValue = vm.resolve?.requireValue ?? null;     // if set, submit only enabled for this value
        vm.useCheckbox = vm.resolve?.useCheckbox ?? false;      // render options as checkboxes (single-select) instead of radios
        vm.requiredMessage = vm.resolve?.requiredMessage ?? null; // if set, show this red error on an invalid submit attempt
        vm.readOnly = vm.resolve?.readOnly ?? false;               // view-only: options disabled, single Close button (no mutation)
        // Preselect: honor defaultValue when the caller supplies it (may be null to force an
        // active choice); otherwise default to the first option.
        vm.choice = (vm.resolve && 'defaultValue' in vm.resolve && vm.resolve.defaultValue !== undefined)
            ? vm.resolve.defaultValue
            : ((vm.options[0] && vm.options[0].value) ?? null);
    };

    // Checkbox mode: sync the string choice from the required checkbox model (ng-model=vm.checked).
    function onCheck(value) {
        vm.choice = vm.checked ? value : null;
    }

    function canSubmit() {
        return vm.requireValue != null ? vm.choice === vm.requireValue : !!vm.choice;
    }
    // Submit is blocked (button disabled) until canSubmit() is true - for the checkbox that means the
    // required box is checked (required + ng-messages mirror confirmation-form). ok() only fires when valid.
    function ok() {
        if (!canSubmit()) {
            return;
        }
        vm.close({
            $value: vm.choice
        });
    }
    function cancel() {
        vm.dismiss({
            $value: 'cancel'
        });
    }
}

export const FormChoiceModal = {
    component: 'apexFormChoiceModal',
    windowClass: 'xx-dialog pagespinner',
    size: 'md',
    backdrop: 'static',
    keyboard: false
};

// Generic gate for state onboarding forms. Shows the choice modal; if the employee picks
// `opts.proceedValue` it resolves true (caller reveals the form). Any other choice shows an
// info message (opts.exitInfo) and resolves false. Callers own "reveal"/"close".
//   opts = { title, prompt?, options:[{value,label}], proceedValue, defaultValue?, exitInfo }
export function openFormChoiceGate($uibModal, opts) {
    return $uibModal.open({
        ...FormChoiceModal,
        resolve: {
            title: () => opts.title,
            prompt: () => opts.prompt,
            options: () => opts.options,
            defaultValue: () => opts.defaultValue ?? opts.proceedValue
        }
    }).result.then(function (choice) {
        if (choice === opts.proceedValue) {
            return true;
        }
        return $uibModal.open({
            ...InfoModal,
            resolve: {
                notification: () => opts.exitInfo,
                showCancel: () => false,
                continue: () => 'OK'
            }
        }).result.then(function () {
            return false;
        });
    });
}
